// src/responses.js
// Marketing segments + the response record (date / method / project / outcome).
//
// No new columns: date and method use marketing_leads.responded_at /
// response_method; the credited project and outcome ride in the lead's
// profile meta (history {type:"profile"}), the credited project's mailing is
// marked responded. A lead that became a pipeline file takes its outcome from
// the file's stage (read-only — the file is the source of truth).
//
// Dates here are LOCAL calendar days (localDay) — never the UTC slice of an
// ISO timestamp, which put every evening response on the next day.

import { leadMetaFrom, leadMetaSet, convertedLoanFor } from './lib';
import { respondedOf, localDay } from './regression';
import { FALLOUT_STAGES, normalizeStage } from './constants';

export const SEGMENTS=[
  {k:"all",l:"All"},
  {k:"hecm",l:"HECM"},
  {k:"prop",l:"Proprietary"},
  {k:"ftr",l:"FTR"},
  {k:"hs2",l:"HomeSafe Second"},
  {k:"other",l:"Other"},
];
export function segmentOf(l){
  const t=(l&&l.tags)||[];
  for(let i=0;i<t.length;i++){
    const x=t[i];
    if(x==="class:refi_hecm")return "hecm";
    if(x==="class:refi_prop")return "prop";
    if(x==="class:ftr"||x==="inquiry-ftr")return "ftr";
    if(x==="class:homesafe_second"||x==="inquiry-hs2")return "hs2";
  }
  return "other";
}

export const RESPONSE_METHODS=["Call","Text","Web form","Email","Other"];
export const OUTCOMES=["Responded","Pitched","Application","Closed","Fallout"];
export const isResponder=respondedOf;
// "— none —" picked on purpose in the response list: this response is not
// credited to any project (and must not fall back to a flagged mailing).
export const NO_PROJECT="none";
// Statuses a response edit must never move backwards to "Responded".
const KEEP_STATUS=/^(Responded|Consultation|Application|Processing|Closed)$/;

// Older writers stored methods that aren't on the list ("Web" from the old
// intake, "AI Chat"/channel names from the chat webhook). Known spellings map
// onto the list; anything else ("Promoted", "Mailer" — not HOW they reached
// out) comes back as-is so the UI can show it and flag it as missing.
const METHOD_ALIASES={"call":"Call","phone":"Call","phone call":"Call","inbound call":"Call","call-in":"Call","call in":"Call","voicemail":"Call",
  "text":"Text","sms":"Text","text message":"Text",
  "web form":"Web form","web":"Web form","webform":"Web form","web intake":"Web form","website":"Web form","form":"Web form",
  "email":"Email","e-mail":"Email",
  "other":"Other","ai chat":"Other","chat":"Other"};
export function normalizeMethod(v){
  const s=String(v==null?"":v).trim();
  if(!s)return "";
  return METHOD_ALIASES[s.toLowerCase()]||s;
}

const WEB_TAGS=["web","inbound-statement","inbound-callback","inbound-inquiry"];
function outcomeFromStage(stage){
  if(!stage)return "";
  stage=normalizeStage(stage); // a pre-10/1 name (Inquiry, CTC…) reads as its new stage
  if(FALLOUT_STAGES.indexOf(stage)>=0)return "Fallout";
  if(stage==="Closed"||stage==="Funded")return "Closed";
  if(stage==="Pitched"||stage==="Prospect")return "Pitched";
  return "Application"; // counseling onward
}
const projectsOf=campaigns=>(campaigns||[]).filter(c=>c&&c.type==="Project");
const lastFlagged=l=>(l.mailings||[]).filter(m=>m&&m.responded).slice(-1)[0]||null;

// The local day a lead responded: responded_at, else the date on the mailing
// it was credited to. "" when nobody recorded one.
export function responseDay(l){
  const d=localDay(l&&l.responded_at);
  if(d)return d;
  const ms=((l&&l.mailings)||[]).filter(m=>m&&m.responded);
  for(let i=ms.length-1;i>=0;i--){const x=localDay(ms[i].respondedDate);if(x)return x;}
  return "";
}

// Everything we know about a lead's response.
export function responseInfo(l,loans,campaigns){
  const meta=leadMetaFrom(l.history);
  const credited=lastFlagged(l);
  const file=convertedLoanFor(l,loans);
  const date=responseDay(l);
  const method=normalizeMethod(l.response_method)||(((l.tags||[]).some(t=>WEB_TAGS.indexOf(t)>=0)||/^(ST|CB|IQ)-/.test(l.id||""))?"Web form":"");
  const methodKnown=RESPONSE_METHODS.indexOf(method)>=0;
  const projectNone=meta.responseProjectId===NO_PROJECT;
  const projectId=projectNone?"":(meta.responseProjectId||(credited&&credited.projectId)||"");
  const project=projectsOf(campaigns).find(p=>p.id===projectId)||null;
  const fileOutcome=file?outcomeFromStage(file.stage):"";
  const outcome=fileOutcome||meta.responseOutcome||(l.status==="Application"?"Application":l.status==="Consultation"?"Pitched":"Responded");
  return{date:date,method:method,methodKnown:methodKnown,projectId:projectId,projectNone:projectNone,projectName:project?project.name:"",outcome:outcome,outcomeLocked:!!fileOutcome,fileId:file?file.id:"",
    missing:[!date&&"date",!methodKnown&&"method",!projectId&&!projectNone&&projectsOf(campaigns).length&&"project"].filter(Boolean)};
}

// Return an updated lead with the response fields set (patch keys: date,
// method, projectId, outcome). Marks the lead responded either way.
// Crediting a project flags THAT project's mailing and un-flags every other
// project mailing, so Mail Performance, the Projects tab and this list all
// agree on one credit. projectId ""/"none" = explicitly no project.
export function applyResponse(l,patch){
  const meta=leadMetaFrom(l.history);
  const next=Object.assign({},l,{responded:true});
  if(!KEEP_STATUS.test(l.status||"")&&!/DNC|Dead|Opt-Out/.test(l.status||""))next.status="Responded";
  if(patch.date!==undefined)next.responded_at=patch.date?patch.date+"T12:00:00Z":null;
  if(patch.method!==undefined)next.response_method=patch.method||null;
  const m2=Object.assign({},meta);
  const day=patch.date!==undefined?(patch.date||""):localDay(next.responded_at);
  if(patch.projectId!==undefined){
    const pid=patch.projectId&&patch.projectId!==NO_PROJECT?patch.projectId:"";
    m2.responseProjectId=pid||NO_PROJECT;
    next.mailings=(l.mailings||[]).map(m=>{
      if(!m||!m.projectId)return m;
      if(pid&&m.projectId===pid)return Object.assign({},m,{responded:true,respondedDate:day||m.respondedDate||""});
      if(m.responded)return Object.assign({},m,{responded:false,respondedDate:""});
      return m;
    });
  }else if(patch.date!==undefined&&meta.responseProjectId&&meta.responseProjectId!==NO_PROJECT){
    // Moving the date keeps the credited mailing's date in step.
    next.mailings=(l.mailings||[]).map(m=>m&&m.projectId===meta.responseProjectId&&m.responded?Object.assign({},m,{respondedDate:day}):m);
  }
  if(patch.outcome!==undefined)m2.responseOutcome=patch.outcome||"";
  if(patch.projectId!==undefined||patch.outcome!==undefined){
    const hist=(Array.isArray(l.history)?l.history:[]).filter(h=>!(h&&h.type==="profile"));
    next.history=leadMetaSet(hist,m2);
  }
  next.updated_at=new Date().toISOString();
  return next;
}

// How one lead stands against one project:
//   "yes"          — counts as a response to it
//   "unattributed" — responded, re-mailed by the project, but the response has
//                    no date and the lead had ALREADY responded before the
//                    project was imported, so it can't be pinned on this drop
//   ""             — not a response to this project
// Explicit credit (the response list) always decides. Otherwise the project's
// own flagged mailing, then the response day vs the drop date.
export function projectVerdict(l,project){
  const pm=(l.mailings||[]).filter(m=>m&&m.projectId===project.id);
  if(!pm.length||!respondedOf(l))return "";
  const pid=leadMetaFrom(l.history).responseProjectId;
  if(pid)return pid===project.id?"yes":"";
  if(pm.some(m=>m.responded))return "yes";
  const drop=localDay(project.mail_date||project.start_date);
  const d=responseDay(l);
  if(d)return !drop||d>=drop?"yes":"";
  if(pm.some(m=>m.priorResponder))return "unattributed";
  // Undated and credited to some other mailing → it answered that one.
  if((l.mailings||[]).some(m=>m&&m.responded))return "";
  return "yes";
}
export function projectResponders(leads,project){
  return (leads||[]).filter(l=>projectVerdict(l,project)==="yes");
}
export function projectUnattributed(leads,project){
  return (leads||[]).filter(l=>projectVerdict(l,project)==="unattributed");
}

// Which of a responder's mailings gets the credit in Mail Performance (an
// index into `ms`, the lead's non-null mailings): the credited project's
// mailing, else the mailing flagged responded, else the latest mailing that
// went out on/before the response day, else the latest dated one. Dates are
// compared as real days — "UNKNOWN (…)" and "9/30/2026" used to sort above
// every ISO date as strings.
export function creditedMailingIndex(l,ms){
  ms=ms||((l&&l.mailings)||[]).filter(Boolean);
  if(!ms.length)return -1;
  const pid=leadMetaFrom(l.history).responseProjectId;
  if(pid&&pid!==NO_PROJECT){for(let i=ms.length-1;i>=0;i--)if(ms[i].projectId===pid)return i;}
  for(let i=ms.length-1;i>=0;i--)if(ms[i].responded)return i;
  const resp=localDay(l.responded_at);
  let before=-1,bd="",latest=-1,ld="";
  ms.forEach((m,i)=>{
    const d=localDay(m.sentAt||m.dateSent);if(!d)return;
    if(d>=ld){latest=i;ld=d;}
    if(resp&&d<=resp&&d>=bd){before=i;bd=d;}
  });
  if(before>=0)return before;
  if(latest>=0)return latest;
  return ms.length-1;
}

// ── CSV imports (Import tab, Bulk Log from CSV) ──
// Headers are matched exact-first, then as WHOLE WORDS ("id" no longer matches
// "video", "st" no longer matches "first name"), skipping headers that are
// clearly something else: "first name" is not the full name, "origination
// date" / "previously_mailed" / "last mailed" are not the mail date.
export function normHeader(h){return String(h==null?"":h).toLowerCase().replace(/^"|"$/g,"").replace(/[_-]/g," ").replace(/\s+/g," ").trim();}
const escRe=s=>s.replace(/[.*+?^${}()|[\]\\]/g,"\\$&");
export function findHeader(headers,names,exclude){
  for(let i=0;i<names.length;i++){const j=headers.indexOf(names[i]);if(j>=0)return j;}
  for(let i=0;i<names.length;i++){
    const re=new RegExp("\\b"+escRe(names[i])+"\\b");
    for(let j=0;j<headers.length;j++){if(exclude&&exclude.test(headers[j]))continue;if(re.test(headers[j]))return j;}
  }
  return -1;
}
export const LEAD_CSV_COLS={
  name:{l:"Name",names:["full name","name","borrower name","borrower","owner name","owner","contact name","contact","mailing name"],exclude:/\b(first|last|middle|fname|lname|company|campaign|lender|servicer|file|list|has|occupied|absentee|type|status|count|phone|email|date|id|address|city|state|zip|source|version|letter|age|dob|birth|flag|indicator)\b/},
  first:{l:"First name",names:["first name","first","fname","given name"],exclude:/\b(last|middle|2)\b/},
  last:{l:"Last name",names:["last name","last","lname","surname"],exclude:/\b(first|mail|mailed|mailing|date|sent|updated|contact|sale|contacted|2)\b/},
  address:{l:"Address",names:["property address","site address","street address","situs address","address","address1","street","property"],exclude:/\b(email|e mail|city|state|zip|unit|apt|2|line 2|value|type|county|use|occupied)\b/},
  city:{l:"City",names:["property city","site city","city"]},
  state:{l:"State",names:["property state","site state","state","st"]},
  zip:{l:"Zip",names:["property zip","site zip","zip","zip code","zipcode","zip5","postal code","postal"]},
  phone:{l:"Phone",names:["phone","phone number","phone 1","phone1","telephone","cell","mobile","contact phone"],exclude:/\b(type|status|dnc|litigator|carrier|2|3)\b/},
  email:{l:"Email",names:["email","email address","e mail","contact email"]},
  lender:{l:"Lender",names:["original lender","orig lender","lender","current lender","servicer","loan servicer"]},
  year:{l:"Close year",names:["close year","origination year","orig year","close date","origination date","orig date","closing date","loan date","close month","origination month","year"],exclude:/\b(built|birth|born|dob|sale|sold|purchase)\b/},
  source:{l:"List source",names:["list source","data source","source"]},
  homeValue:{l:"Home value",names:["home value","property value","estimated value","market value","appraised value","avm","value"],exclude:/\b(loan|ltv|to)\b/},
  balance:{l:"Loan balance",names:["loan balance","mortgage balance","unpaid balance","current balance","balance","upb","loan amount","original balance","mca","max claim"]},
  loanType:{l:"Loan type",names:["loan type","mortgage type","type","product"],exclude:/\b(property|envelope|mail|mailing|phone|record|dwelling|owner|letter|stamp|return)\b/},
  id:{l:"Lead ID",names:["lead id","marketing id","mk id","id"],exclude:/\b(sl|loan|tax|parcel|apn|record|batch|campaign|mailing|file|property|fips)\b/},
  sl:{l:"SL ID",names:["sl id","slid"]},
  campaign:{l:"Campaign",names:["campaign","campaign name","campaigns"]},
  mailDate:{l:"Mail date",names:["mail date","date sent","sent date","date mailed","mailed date","mailed on","drop date"],exclude:/\b(origination|orig|close|closing|purchase|loan|birth|dob|rec|recorded|generated|last|previously|prior|next|follow|sale|built)\b/},
};
export function pickLeadCsvCols(rawHeaders){
  const h=(rawHeaders||[]).map(normHeader);
  const out={};
  Object.keys(LEAD_CSV_COLS).forEach(k=>{const c=LEAD_CSV_COLS[k];out[k]=findHeader(h,c.names,c.exclude);});
  return out;
}
// "Name: Owner Name · Address: Property Address · …" for the preview.
export function describeCsvCols(cols,rawHeaders,keys){
  return (keys||Object.keys(LEAD_CSV_COLS)).filter(k=>cols[k]>=0).map(k=>LEAD_CSV_COLS[k].l+": "+rawHeaders[cols[k]]).join(" · ");
}
// A per-row mail date only overrides the form's date when it IS a date.
export function csvDate(v){return localDay(String(v==null?"":v).trim());}

const US_STATES="AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY".split(" ");
export function zip5(z){const m=/\d{5}/.exec(String(z||""));return m?m[0]:"";}
const isZipTok=t=>/^\d{5}(-\d{4})?$/.test(t);
const isStateTok=t=>US_STATES.indexOf(String(t).toUpperCase())>=0;
function isStateZipSeg(p){const t=p.split(/\s+/);if(t.length===1)return isZipTok(t[0])||isStateTok(t[0]);return t.length===2&&isStateTok(t[0])&&isZipTok(t[1]);}
function isCityStateZipSeg(p){const t=p.split(/\s+/);let k=t.length;if(k&&isZipTok(t[k-1]))k--;if(k<2||!isStateTok(t[k-1]))return false;return !/\d/.test(t.slice(0,k-1).join(" "));}
// Street part of an address that may carry ", City, ST, 12345" on the end.
// Leads store the street alone (city/state/zip have their own columns), so a
// file that folds them in must be cut back before it can match. Only trailing
// state / zip / "City ST ZIP" segments (and the one city segment before them,
// or a segment equal to `city`) are cut — "Apt 2" and "1,771 Sqft. Dr" stay.
export function streetOnly(a,city){
  const s=String(a||"").trim();
  const parts=s.split(",").map(x=>x.trim()).filter(Boolean);
  if(parts.length<2||!/^\d/.test(parts[0]))return s;
  const c=String(city||"").trim().toLowerCase();
  let n=parts.length,cut=0;
  while(n>1&&isStateZipSeg(parts[n-1])){n--;cut++;}
  if(n>1&&!cut&&isCityStateZipSeg(parts[n-1]))n--;
  else if(n>1&&cut&&(c?parts[n-1].toLowerCase()===c:!/\d/.test(parts[n-1])))n--;
  else if(n>1&&c&&parts[n-1].toLowerCase()===c)n--;
  return n===parts.length?s:parts.slice(0,n).join(", ");
}
export function normStreet(a){
  return String(streetOnly(a)).toUpperCase().replace(/[.,#]/g," ").replace(/\bSTREET\b/g,"ST").replace(/\bAVENUE\b/g,"AVE").replace(/\bDRIVE\b/g,"DR").replace(/\bROAD\b/g,"RD").replace(/\bLANE\b/g,"LN").replace(/\bCOURT\b/g,"CT").replace(/\bBOULEVARD\b/g,"BLVD").replace(/\bPLACE\b/g,"PL").replace(/\bCIRCLE\b/g,"CIR").replace(/\bTERRACE\b/g,"TER").replace(/\bPARKWAY\b/g,"PKWY").replace(/\bHIGHWAY\b/g,"HWY").replace(/\bNORTH\b/g,"N").replace(/\bSOUTH\b/g,"S").replace(/\bEAST\b/g,"E").replace(/\bWEST\b/g,"W").replace(/\s+/g," ").trim();
}
export function nameKey(n){return String(n||"").toLowerCase().replace(/[^a-z0-9]/g,"");}
const NAME_NOISE={and:1,the:1,trust:1,trustee:1,trustees:1,estate:1,living:1,family:1,revocable:1,mr:1,mrs:1,ms:1,jr:1,sr:1,etal:1};
function nameTokens(n){return String(n||"").toLowerCase().split(/[^a-z]+/).filter(t=>t.length>=3&&!NAME_NOISE[t]);}
function sharesNameToken(a,b){const ta=nameTokens(a);if(!ta.length)return false;const tb=nameTokens(b);return tb.some(t=>ta.indexOf(t)>=0);}
// Key for "same person at the same street" (dedupe within a file).
export function personKey(name,address){return nameKey(name)+"|"+normStreet(address);}
// Match CSV rows to existing leads: Lead ID (or a merged id), SL ID, then name
// + street (zip only breaks ties — the same street is stored under two zips in
// places), then street + zip when the names share a word ("Robert Johnson" vs
// "Johnson, Robert & Mary").
export function buildLeadMatcher(leads){
  const byId=new Map(),bySl=new Map(),byMerged=new Map(),byKey=new Map(),byStreetZip=new Map();
  const push=(map,k,l)=>{const a=map.get(k);if(a)a.push(l);else map.set(k,[l]);};
  (leads||[]).forEach(l=>{
    if(!l||!l.id)return;
    byId.set(l.id,l);
    (l.tags||[]).forEach(t=>{t=String(t||"");if(/^sl:/.test(t))bySl.set(t.slice(3),l);else if(/^merged:/.test(t))byMerged.set(t.slice(7),l);});
    const st=normStreet(l.address);if(!st)return;
    push(byKey,nameKey(l.name)+"|"+st,l);
    const z=zip5(l.zip);if(z)push(byStreetZip,st+"|"+z,l);
  });
  return function match(row){
    const id=String(row.id||"").trim(),sl=String(row.sl||"").trim();
    const hit=(id&&(byId.get(id)||byMerged.get(id)))||(sl&&bySl.get(sl));
    if(hit)return hit;
    const st=normStreet(row.address);if(!st)return null;
    const z=zip5(row.zip);
    const c=byKey.get(nameKey(row.name)+"|"+st);
    if(c&&c.length)return (z&&c.length>1&&c.find(x=>zip5(x.zip)===z))||c[0];
    if(z){const c2=(byStreetZip.get(st+"|"+z)||[]).filter(x=>sharesNameToken(x.name,row.name));if(c2.length===1)return c2[0];}
    return null;
  };
}
